
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BlogPost } from '@/types/blog';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';

interface BlogPostPreviewProps {
  post: BlogPost;
  isOpen: boolean;
  onClose: () => void;
}

const BlogPostPreview: React.FC<BlogPostPreviewProps> = ({
  post,
  isOpen,
  onClose
}) => {
  if (!post) return null;
  
  const formattedDate = post.date ? format(parseISO(post.date), 'MMMM dd, yyyy') : '';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Blog Post Preview
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <div className="mb-6">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
          
          <div className="flex justify-between items-center mb-4">
            <Badge className={post.status === 'published' ? 'bg-green-500' : 
                           post.status === 'draft' ? 'bg-amber-500' : 'bg-blue-500'}>
              {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
            </Badge>
            <span className="text-gray-500 text-sm">
              {formattedDate} • {post.readTime} read
            </span>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{post.title}</h1>
          
          <div className="flex items-center mb-6">
            <div className="h-10 w-10 flex-shrink-0 mr-3">
              <img className="h-10 w-10 rounded-full" src="/assets/e2e58de0-5228-4c21-8754-a52246e544e0.png" alt={post.author} />
            </div>
            <div>
              <p className="font-medium">{post.author}</p>
              <p className="text-sm text-gray-500">Content Author</p>
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none">
            {post.content && post.content.split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4">{paragraph}</p>
            ))}
          </div>
          
          <div className="border-t mt-8 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-500 mr-2">Category:</span>
                <Badge variant="outline" className="text-sm">{post.category}</Badge>
              </div>
              <div>
                <span className="text-sm text-gray-500 mr-2">Share:</span>
                {post.socialShare ? 
                  <Badge variant="secondary" className="text-sm">Enabled</Badge> : 
                  <Badge variant="outline" className="text-sm">Disabled</Badge>}
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={onClose}>Close Preview</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogPostPreview;
